import React, { useState, useEffect, KeyboardEvent } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  useDroppable,
  DragStartEvent,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useTranslation } from "react-i18next";
import StageForm from "../components/StageForm";
import { axiosInstance } from "../services/api";
import { canDeleteIdea } from "../utils/authHelper";
import { getCurrentUser } from "../utils/auth";
import { authFetch } from "../utils/authFetch";
import "./KanbanBoard.css";

interface Stage {
  id: string;
  title: string;
  color: string;
}

interface Idea {
  id: number | string;
  title: string;
  description?: string;
  priority: string;
  stage: string;
  owner_name?: string;
  owner_id?: number;
  department?: string;
  tags?: string[] | string;
}

interface CurrentUser {
  id: number;
  role: string;
}

interface IdeaFormData {
  title: string;
  description?: string;
  priority: string;
  stage?: string;
  department?: string;
  tags?: string[];
}

interface DroppableColumnProps {
  stage: Stage;
  children: React.ReactNode;
  ideaCount?: number;
}

interface SortableIdeaCardProps {
  idea: Idea;
  getPriorityColor: (priority: string) => string;
  onEdit?: (idea: Idea) => void;
  onDelete?: (ideaId: number | string) => void;
}

interface DragOverlayCardProps {
  idea: Idea;
  getPriorityColor: (priority: string) => string;
}

// Droppable Column Component
const DroppableColumn: React.FC<DroppableColumnProps> = ({
  stage,
  children,
  ideaCount = 0,
}) => {
  const { setNodeRef, isOver } = useDroppable({
    id: stage.id,
  });

  return (
    <div
      ref={setNodeRef}
      className={`kanban-column ${isOver ? "dropping" : ""}`}
    >
      <div className="column-header" style={{ backgroundColor: stage.color }}>
        <h3>{stage.title}</h3>
        <span className="idea-count">{ideaCount}</span>
      </div>

      <div className="column-content">{children}</div>
    </div>
  );
};

// Sortable Idea Card Component
const SortableIdeaCard: React.FC<SortableIdeaCardProps> = ({
  idea,
  getPriorityColor,
  onEdit,
  onDelete,
}) => {
  const { t } = useTranslation(["recruiting", "common"]);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: idea.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleClick = () => {
    // Don't trigger edit when dragging
    if (!isDragging && onEdit) {
      onEdit(idea);
    }
  };

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering the card click
    e.preventDefault(); // Prevent any default behavior
    if (onEdit) {
      onEdit(idea);
    }
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering the card click
    e.preventDefault(); // Prevent any default behavior
    if (
      onDelete &&
      window.confirm(t("kanban.confirmDelete", { title: idea.title }))
    ) {
      onDelete(idea.id);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter" || e.key === " ") {
      handleClick();
    }
  };

  // Get current user info for permission check
  const currentUser = (getCurrentUser() || {}) as CurrentUser;
  const canDelete = canDeleteIdea(idea, currentUser);

  // Parse tags
  const tags: string[] = Array.isArray(idea.tags)
    ? idea.tags
    : typeof idea.tags === "string" && idea.tags.trim()
      ? idea.tags
          .split(",")
          .map((tag) => tag.trim())
          .filter((tag) => tag)
      : [];

  return (
    <div className="idea-card-wrapper">
      {/* Action buttons outside of draggable area */}
      <div className="action-buttons-absolute">
        <button
          className="edit-btn-absolute"
          onClick={handleEditClick}
          title={t("kanban.editIdea")}
        >
          ✏️
        </button>
        {canDelete && (
          <button
            className="delete-btn-absolute"
            onClick={handleDeleteClick}
            title={t("kanban.deleteIdea")}
          >
            🗑️
          </button>
        )}
      </div>

      {/* Draggable card content */}
      <div
        ref={setNodeRef}
        style={style}
        {...attributes}
        {...listeners}
        className={`idea-card ${isDragging ? "dragging" : ""}`}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        role="button"
        tabIndex={0}
      >
        <div className="idea-header">
          <div className="idea-title-section">
            <h4>{idea.title}</h4>
          </div>
          <div
            className="priority-badge"
            style={{ backgroundColor: getPriorityColor(idea.priority) }}
          >
            {idea.priority}
          </div>
        </div>

        <p className="idea-description">
          {idea.description?.substring(0, 100)}
          {idea.description && idea.description.length > 100 && "..."}
        </p>

        <div className="idea-meta">
          <span className="owner">
            {t("kanban.by")}: {idea.owner_name || t("common:unknown")}
          </span>
          {idea.department && (
            <span className="department">{idea.department}</span>
          )}
        </div>

        {tags.length > 0 && (
          <div className="idea-tags">
            {tags.slice(0, 3).map((tag) => (
              <span key={`${idea.id}-${tag}`} className="tag">
                {tag}
              </span>
            ))}
            {tags.length > 3 && (
              <span className="tag-more">+{tags.length - 3}</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Drag Overlay Component (shows while dragging)
const DragOverlayCard: React.FC<DragOverlayCardProps> = ({
  idea,
  getPriorityColor,
}) => {
  const { t } = useTranslation(["recruiting", "common"]);

  // Parse tags
  const tags: string[] = Array.isArray(idea.tags)
    ? idea.tags
    : typeof idea.tags === "string" && idea.tags.trim()
      ? idea.tags
          .split(",")
          .map((tag) => tag.trim())
          .filter(Boolean)
      : [];

  return (
    <div className="idea-card dragging-overlay">
      <div className="idea-header">
        <h4>{idea.title}</h4>
        <div
          className="priority-badge"
          style={{ backgroundColor: getPriorityColor(idea.priority) }}
        >
          {idea.priority}
        </div>
      </div>

      <p className="idea-description">
        {idea.description?.substring(0, 100)}
        {idea.description && idea.description.length > 100 && "..."}
      </p>

      <div className="idea-meta">
        <span className="owner">
          {t("kanban.by")}: {idea.owner_name || t("common:unknown")}
        </span>
        {idea.department && (
          <span className="department">{idea.department}</span>
        )}
      </div>

      {tags.length > 0 && (
        <div className="idea-tags">
          {tags.slice(0, 3).map((tag) => (
            <span key={`overlay-${idea.id}-${tag}`} className="tag">
              {tag}
            </span>
          ))}
          {tags.length > 3 && (
            <span className="tag-more">+{tags.length - 3}</span>
          )}
        </div>
      )}
    </div>
  );
};

const KanbanBoard: React.FC = () => {
  const { t } = useTranslation(["recruiting", "common"]);
  const [ideas, setIdeas] = useState<Idea[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingIdea, setEditingIdea] = useState<Idea | null>(null);
  const [activeId, setActiveId] = useState<number | string | null>(null);

  // Add full-width class to main content when component mounts
  useEffect(() => {
    const mainContent = document.querySelector(".main-content");
    if (mainContent) {
      mainContent.classList.add("kanban-full-width");
    }

    // Cleanup function to remove the class when component unmounts
    return () => {
      if (mainContent) {
        mainContent.classList.remove("kanban-full-width");
      }
    };
  }, []);

  const stages: Stage[] = [
    { id: "Ideation", title: "Ideation", color: "#e3f2fd" },
    { id: "Research", title: "Research", color: "#fff3e0" },
    { id: "PoC", title: "PoC", color: "#f3e5f5" },
    { id: "Marketing", title: "Marketing", color: "#e8f5e8" },
    { id: "Sales", title: "Sales", color: "#fff8e1" },
    { id: "Validated", title: "Validated", color: "#e0f2f1" },
  ];

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // Fetch ideas from API
  const fetchIdeas = async () => {
    try {
      setLoading(true);
      console.log("Fetching ideas from API...");

      const response = await axiosInstance.get("/kanban/ideas");
      console.log("API response:", response.data);

      if (response.data.success) {
        console.log("Setting ideas:", response.data.data);
        // Debug: Log the first idea to see its structure
        if (response.data.data && response.data.data.length > 0) {
          console.log("First idea structure:", response.data.data[0]);
          console.log("Owner name:", response.data.data[0].owner_name);
          console.log("Owner ID:", response.data.data[0].owner_id);
        }
        setIdeas(response.data.data || []);
      } else {
        throw new Error(
          response.data.error ||
            response.data.message ||
            "Failed to fetch ideas",
        );
      }
    } catch (err) {
      console.error("Error fetching ideas:", err);
      const error = err as {
        response?: { data?: { error?: string; message?: string } };
        message?: string;
      };
      setError(
        error.response?.data?.error ||
          error.response?.data?.message ||
          error.message ||
          "Unknown error",
      );
    } finally {
      setLoading(false);
    }
  };

  // Update idea stage
  const updateIdeaStage = async (ideaId: number | string, newStage: string) => {
    try {
      const response = await authFetch(`/api/kanban/ideas/${ideaId}/stage`, {
        method: "PATCH",
        body: JSON.stringify({ stage: newStage }),
      });

      if (!response.ok) {
        throw new Error("Failed to update idea stage");
      }

      const data = await response.json();
      if (data.success) {
        setIdeas((prev) =>
          prev.map((idea) =>
            idea.id === ideaId ? { ...idea, stage: newStage } : idea,
          ),
        );
      } else {
        throw new Error(
          data.error || data.message || "Failed to update idea stage",
        );
      }
    } catch (err) {
      console.error("Error updating idea stage:", err);
      setError((err as Error).message);
    }
  };

  // Handle drag start
  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    setActiveId(active.id as number | string);
  };

  // Handle drag end
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    console.log("Drag end:", { active, over });

    setActiveId(null);

    if (!over) {
      console.log("No drop target");
      return;
    }

    const activeIdValue = active.id as number | string;
    const overId = over.id as string;

    if (activeIdValue === overId) {
      console.log("Same position, no change");
      return;
    }

    // Find the active idea
    const activeIdea = ideas.find((idea) => idea.id === activeIdValue);
    if (!activeIdea) {
      console.log("Active idea not found");
      return;
    }

    // Check if dropping on a stage (column)
    const targetStage = stages.find((stage) => stage.id === overId);
    if (targetStage) {
      console.log("Moving to stage:", targetStage.id);
      updateIdeaStage(activeIdValue, targetStage.id);
      return;
    }

    // Check if dropping on another idea
    const overIdea = ideas.find((idea) => idea.id === overId);
    if (overIdea) {
      // If same stage, reorder
      if (activeIdea.stage === overIdea.stage) {
        console.log("Reordering within same stage");
        const oldIndex = ideas.findIndex((idea) => idea.id === activeIdValue);
        const newIndex = ideas.findIndex((idea) => idea.id === overId);

        setIdeas((prev) => arrayMove(prev, oldIndex, newIndex));
      } else {
        // Different stage, move to that stage
        console.log("Moving to stage of idea:", overIdea.stage);
        updateIdeaStage(activeIdValue, overIdea.stage);
      }
      return;
    }
  };

  // Handle saving idea (create or update)
  const handleSaveIdea = async (formData: IdeaFormData) => {
    try {
      if (editingIdea) {
        // Update existing idea
        const response = await authFetch(
          `/api/kanban/ideas/${editingIdea.id}`,
          {
            method: "PUT",
            body: JSON.stringify(formData),
          },
        );

        if (!response.ok) {
          throw new Error("Failed to update idea");
        }
      } else {
        // Create new idea
        const response = await authFetch("/api/kanban/ideas", {
          method: "POST",
          body: JSON.stringify(formData),
        });

        if (!response.ok) {
          throw new Error("Failed to create idea");
        }
      }

      setShowCreateModal(false);
      setEditingIdea(null);
      fetchIdeas();
    } catch (err) {
      console.error("Error saving idea:", err);
      setError((err as Error).message);
    }
  };

  const handleCancelEdit = () => {
    setShowCreateModal(false);
    setEditingIdea(null);
  };

  const handleEditIdea = (idea: Idea) => {
    setEditingIdea(idea);
    setShowCreateModal(true);
  };

  const handleDeleteIdea = async (ideaId: number | string) => {
    try {
      await axiosInstance.delete(`/kanban/ideas/${ideaId}`);
      await fetchIdeas(); // Refresh the ideas list
    } catch (err) {
      console.error("Error deleting idea:", err);
      alert(t("kanban.deleteError"));
    }
  };

  // Get ideas for a specific stage
  const getIdeasForStage = (stageId: string): Idea[] => {
    const stageIdeas = ideas.filter((idea) => idea.stage === stageId);
    console.log(`Ideas for stage ${stageId}:`, stageIdeas);
    return stageIdeas;
  };

  // Get priority color
  const getPriorityColor = (priority: string): string => {
    const colors: Record<string, string> = {
      low: "#4caf50",
      medium: "#ff9800",
      high: "#f44336",
      critical: "#9c27b0",
    };
    return colors[priority] || colors.medium;
  };

  // Get active idea for overlay
  const activeIdea = activeId
    ? ideas.find((idea) => idea.id === activeId)
    : null;

  useEffect(() => {
    fetchIdeas();
  }, []);

  if (loading) {
    return (
      <div className="kanban-loading">
        <div className="loading-spinner"></div>
        <p>{t("kanban.loading")}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="kanban-error">
        <h2>{t("common:error")}</h2>
        <p>{error}</p>
        <button onClick={fetchIdeas}>{t("common:retry")}</button>
      </div>
    );
  }

  console.log("Rendering kanban board with ideas:", ideas);
  console.log("Ideas count:", ideas.length);

  return (
    <div className="kanban-board">
      <div className="kanban-header">
        <div className="kanban-brand">
          <h1>{t("kanban.title")}</h1>
        </div>
        <button
          className="create-idea-btn"
          onClick={() => {
            setEditingIdea(null);
            setShowCreateModal(true);
          }}
        >
          + {t("kanban.newIdea")}
        </button>
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <div className="kanban-columns">
          {stages.map((stage) => {
            const stageIdeas = getIdeasForStage(stage.id);
            console.log(
              `Rendering stage ${stage.id} with ${stageIdeas.length} ideas`,
            );

            return (
              <DroppableColumn
                key={stage.id}
                stage={stage}
                ideaCount={stageIdeas.length}
              >
                <SortableContext
                  items={stageIdeas.map((idea) => idea.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {stageIdeas.map((idea) => (
                    <SortableIdeaCard
                      key={idea.id}
                      idea={idea}
                      getPriorityColor={getPriorityColor}
                      onEdit={handleEditIdea}
                      onDelete={handleDeleteIdea}
                    />
                  ))}
                </SortableContext>
              </DroppableColumn>
            );
          })}
        </div>

        <DragOverlay>
          {activeIdea ? (
            <DragOverlayCard
              idea={activeIdea}
              getPriorityColor={getPriorityColor}
            />
          ) : null}
        </DragOverlay>
      </DndContext>

      {/* Stage Form Modal */}
      {showCreateModal && (
        <StageForm
          idea={editingIdea}
          onSave={handleSaveIdea}
          onCancel={handleCancelEdit}
          onDelete={fetchIdeas}
        />
      )}
    </div>
  );
};

export default KanbanBoard;
